<template>
  <div v-if="ready" ref="strip" class="s-fleet-flexdrive">
    <div class="page-container">
      <div class="s-fleet-flexdrive__header mb-30">
        <div class="title h2">{{ title }}</div>

        <div v-if="showClearFilters" class="d-none d-lg-flex">
          <button
            type="button"
            class="btn btn-clear btn-icon-start"
            @click="onClickClearFilters">
            <i class="icon-bin"></i>
            {{ dictionary.buttonClearFiltersText }}
          </button>
        </div>
      </div>

      <div class="b-accordion" :class="{ 'is-active': showAccordionBody }">
        <div class="b-accordion__header" @click="onClickAccordionHeader">
          <span>{{ dictionary.accordionFiltersTitle }}</span>
          <i class="icon-arrow-dropdown-down"></i>
        </div>
        <div v-show="showAccordionBody" class="b-accordion__body">
          <div class="row">
            <div class="col-lg mb-20">
              <FilterBrand
                v-if="records"
                ref="filterBrand"
                :records="records"
                @change-selected="onChangeBrand" />
            </div>
            <div class="col-lg mb-20">
              <FilterModel
                v-if="records"
                ref="filterModel"
                :records="records"
                @change-selected="onChangeModel" />
            </div>
            <div class="col-lg mb-20">
              <FilterFuel
                v-if="records"
                ref="filterFuel"
                :records="records"
                @change-selected="onChangeFuel" />
            </div>
            <div class="col-lg mb-20">
              <FilterTransmission
                v-if="records"
                ref="filterTransmission"
                :records="records"
                @change-selected="onChangeTransmission" />
            </div>
            <div class="col-lg mb-20">
              <FilterPrice
                v-if="records"
                ref="filterPrice"
                :records="records"
                :min-value="priceMinValue"
                :max-value="priceMaxValue"
                @change-selected="onChangePrice" />
            </div>
          </div>

          <div>
            <div v-if="showClearFilters" class="d-flex d-lg-none clear-all-btn">
              <button
                type="button"
                class="btn btn-clear btn-icon-start"
                @click="onClickClearFilters">
                <i class="icon-bin"></i>
                {{ dictionary.buttonClearFiltersText }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="s-fleet-flexdrive__separator mb-20"></div>

      <div class="row s-fleet-flexdrive__tools">
        <div class="col-md-6 col-xl-8 mb-40 d-flex align-items-center">
          <strong>
            {{ recordsCountFiltered }} {{ dictionary.resultsFoundSuffix }}
          </strong>
        </div>
        <div class="col-md-6 col-xl-4">
          <div class="row">
            <div class="col-8 col-lg-9 mb-40" style="flex: 1">
              <select
                v-model="sort"
                class="form-select form-select-short"
                @change="onChangeSort">
                <option
                  v-for="item in sortOptions"
                  :key="item.value"
                  :value="item.value">
                  {{ item.text }}
                </option>
              </select>
            </div>
            <div class="col-4 col-lg-3 mb-40" style="flex-basis: fit-content">
              <select
                v-model="itemsPerPage"
                class="form-select form-select-short"
                @change="onChangeItemsPerPage">
                <option
                  v-for="item in itemsPerPageOptions"
                  :key="item.value"
                  :value="item.value">
                  {{ item.text }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="recordsCountFiltered"
        ref="grid"
        v-auto-animate
        class="s-fleet-flexdrive__grid">
        <div
          v-for="item in recordsFilteredPaged"
          :key="item.id"
          class="s-fleet-flexdrive__item">
          <VehicleFlexDrive :item="item" />
        </div>
      </div>

      <div v-if="!recordsCountFiltered">
        <NoResults></NoResults>
      </div>

      <div class="s-fleet-flexdrive__disclaimer">
        {{ dictionary.galleryDisclaimer }}
      </div>

      <div v-if="recordsCountFiltered" class="d-flex justify-content-center">
        <PaginationAsViewMore
          :total-records="recordsCountFiltered"
          :per-page="itemsPerPage"
          :current-page="page"
          @pagechanged="pageChanged" />
      </div>

      <Anchor
        v-if="clickToCall"
        :item="clickToCall"
        :hide-text="true"
        aria-label="Click to call"
        class="e-call-us body-fixed">
        <i class="icon-phone-call"></i>
      </Anchor>
    </div>
  </div>
</template>

<script>
import jQuery from 'jquery'
import * as lib from '../../lib/lib'
import VehicleFlexDrive from '../components/VehicleFlexDriveV2.vue'
import NoResults from '../components/common/NoResults.vue'
import PaginationAsViewMore from './../components/common/PaginationAsViewMore.vue'
/* eslint-disable no-unused-vars */
import { compact, max, min, orderBy, sortBy, uniqBy } from 'lodash'
import FilterBrand from './../components/flexdrive/FilterBrand.vue'
import FilterModel from './../components/flexdrive/FilterModel.vue'
import FilterTransmission from './../components/flexdrive/FilterTransmission.vue'
import FilterFuel from './../components/flexdrive/FilterFuel.vue'
import FilterPrice from './../components/flexdrive/FilterPrice.vue'
import mixinsFlexDrive from './../mixins/flexdrive-mixins'
import constants from './../../lib/constants'
import Anchor from './../components/common/Anchor.vue'

const { filters } = constants.flexdrive

export default {
  components: {
    Anchor,
    NoResults,
    VehicleFlexDrive,
    PaginationAsViewMore,
    FilterBrand,
    FilterModel,
    FilterTransmission,
    FilterFuel,
    FilterPrice,
  },
  mixins: [mixinsFlexDrive],
  data() {
    return {
      ready: false,
      title: '',
      records: [],
      recordsCount: null,
      recordsFiltered: [],
      page: 1,
      itemsPerPage: 0,
      sort: '',
      showAccordionBody: false,
      // data sources
      sortOptions: [],
      itemsPerPageOptions: [],
      clickToCall: null,
    }
  },
  computed: {
    showClearFilters() {
      return (
        this.brands.length ||
        this.models.length ||
        this.fuels.length ||
        this.transmissions.length ||
        (this.prices.length && this.pricesHaveChanged)
      )
    },

    pricesHaveChanged() {
      return (
        Number.parseInt(this.prices[0]) !== this.priceMinValue ||
        Number.parseInt(this.prices[1]) !== this.priceMaxValue
      )
    },

    recordsFilteredPaged() {
      // const start = (this.page - 1) * this.itemsPerPage
      const start = 0
      const end = this.page * this.itemsPerPage
      return this.recordsFiltered.slice(start, end)
    },

    recordsCountFiltered() {
      return this.recordsFiltered.length
    },

    priceMinValue() {
      const { priceInterval } = this.configuration
      const items = this.records.map((i) => i.pricePerMonthInteger)

      /* eslint-disable no-undef */
      return Math.floor(_.min(items) / priceInterval) * priceInterval
    },

    priceMaxValue() {
      const { priceInterval } = this.configuration
      const items = this.records.map((i) => i.pricePerMonthInteger)

      /* eslint-disable no-undef */
      return Math.ceil(_.max(items) / priceInterval) * priceInterval
    },
  },
  mounted() {
    const { title, records, sortOptions, itemsPerPageOptions, clickToCall } =
      lib.getData('fleetData')
    this.title = title
    this.records = this.recordsFiltered = records
    this.sortOptions = sortOptions
    this.itemsPerPageOptions = itemsPerPageOptions
    this.clickToCall = clickToCall

    this.addOriginalIndex()
    this.setPreselectedOptions()
    this.setPreselectedValues()

    this.ready = true
    this.accordionIsActive = lib.scrDesktops()

    const hash = window.location.hash
    this.$nextTick(() => {
      if (hash === '#selectVehicle') {
        setTimeout(() => {
          lib.scrollToElem(jQuery('#section-fleet-flexdrive'), 150)
        }, 500)
      }
      this.onChangeSort()
    })
  },
  methods: {
    onClickAccordionHeader() {
      this.showAccordionBody = !this.showAccordionBody
    },

    addOriginalIndex() {
      /* eslint-disable no-undef */
      _.forEach(this.records, (x, index) => {
        x.originalIndex = index
      })
    },

    onChangeBrand(value) {
      lib.appendUrlParam(filters.brands, value.join(','))
      this.setField({ field: filters.brands, value })
      if (!value.length) {
        this.$refs.filterModel.resetSelected()
      }

      this.filterRecords()
    },

    onChangeModel(value) {
      lib.appendUrlParam(filters.models, value.join(','))
      this.setField({ field: filters.models, value })
      this.filterRecords()
    },

    onChangeFuel(value) {
      lib.appendUrlParam(filters.fuels, value.join(','))
      this.setField({ field: filters.fuels, value })
      this.filterRecords()
    },

    onChangeTransmission(value) {
      lib.appendUrlParam(filters.transmissions, value.join(','))
      this.setField({ field: filters.transmissions, value })
      this.filterRecords()
    },

    onChangePrice(value) {
      lib.appendUrlParam(filters.prices, value.join(','))
      this.setField({ field: filters.prices, value })
      this.filterRecords()
    },

    filterRecords() {
      this.page = 1
      let array = this.records

      if (this.brands.length) {
        array = array.filter((i) => this.brands.includes(i.make))
      }

      if (this.models.length) {
        array = array.filter((i) => this.models.includes(i.model))
      }

      if (this.fuels.length) {
        array = array.filter((i) => this.fuels.includes(i.fuelPreValue))
      }

      if (this.transmissions.length) {
        array = array.filter((i) =>
          this.transmissions.includes(i.transmissionTypePreValue)
        )
      }

      if (this.prices.length) {
        array = array.filter(
          (i) =>
            i.pricePerMonthInteger >= this.prices[0] &&
            i.pricePerMonthInteger <= this.prices[1]
        )
      }

      this.recordsFiltered = array
    },

    setPreselectedOptions() {
      // sort
      const entry = this.sortOptions.find((i) => i.selected)
      if (entry) {
        this.sort = entry.value
        this.onChangeSort()
      }
      // items per page
      const entry2 = this.itemsPerPageOptions.find((i) => i.selected)
      if (entry) {
        this.itemsPerPage = entry2.value
      }

      this.itemsPerPageOptions.push({
        text: this.dictionary.itemsPerPageAllLabel,
        value: this.records.length,
      })
    },

    setPreselectedValues() {
      this.setPreselectedBrands()
      this.setPreselectedModels()
      this.setPreselectedTransmissions()
      this.setPreselectedFuels()
      this.setPreselectedPrices()
      this.filterRecords()
    },

    setPreselectedBrands() {
      const field = filters.brands
      const urlParam = lib.getUrlParam(field)
      const value = urlParam
        ? urlParam
            .split(',')
            .filter((i) => this.records.some((x) => x.make === i))
        : []

      this.setField({ field, value })
    },

    setPreselectedModels() {
      const field = filters.models
      const urlParam = lib.getUrlParam(field)
      const value = urlParam
        ? urlParam
            .split(',')
            .filter((i) => this.records.some((x) => x.model === i))
        : []

      this.setField({ field, value })
    },

    setPreselectedTransmissions() {
      const field = filters.transmissions
      const urlParam = lib.getUrlParam(field)
      const value = urlParam
        ? urlParam
            .split(',')
            .filter((i) =>
              this.records.some((x) => x.transmissionTypePreValue === i)
            )
        : []

      this.setField({ field, value })
    },

    setPreselectedFuels() {
      const field = filters.fuels
      const urlParam = lib.getUrlParam(field)
      const value = urlParam
        ? urlParam
            .split(',')
            .filter((i) => this.records.some((x) => x.fuelPreValue === i))
        : []

      this.setField({ field, value })
    },

    setPreselectedPrices() {
      const field = filters.prices
      const urlParam = lib.getUrlParam(field)
      const value = urlParam ? urlParam.split(',') : []

      this.setField({ field, value })
    },

    pageChanged(value) {
      this.page = value
    },

    onChangeSort() {
      this.page = 1
      const { ascending, descending, featured } = constants.sort

      let array = this.recordsFiltered

      switch (this.sort) {
        case ascending:
          array = _.orderBy(array, 'pricePerMonthInteger', 'asc')
          break
        case descending:
          array = _.orderBy(array, 'pricePerMonthInteger', 'desc')
          break
        case featured:
          array = _.sortBy(array, (x) => x.originalIndex)
          break
        default:
          array = _.sortBy(array, (x) => x.originalIndex)
      }

      this.recordsFiltered = array
    },

    onChangeItemsPerPage() {
      this.page = 1
    },

    onClickClearFilters() {
      this.brands = this.models = this.fuels = this.transmissions = []

      this.$refs.filterBrand.resetSelected()
      this.$refs.filterModel.resetSelected()
      this.$refs.filterFuel.resetSelected()
      this.$refs.filterTransmission.resetSelected()
      this.$refs.filterPrice.resetSelected()
    },
  },
}
</script>
